import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { createContext, forwardRef, useCallback, useContext, useId, useRef } from 'react'
import { Loader2, X as XIcon } from 'lucide-react'
import { cn } from './cn'
import { InfoTooltip } from './info-tooltip'

export type InputSize = 'sm' | 'md' | 'lg'

type FieldCtx = {
  /** id forwarded to the input/select/textarea — also used by the label's `htmlFor`. */
  id: string
  /** id of the description element (error or hint) — wired into `aria-describedby`. */
  describedBy: string | undefined
  invalid: boolean
  /** When true, child primitives auto-wire `aria-required`. */
  required: boolean
}

const FieldContext = createContext<FieldCtx | null>(null)

/** Field wrapper — label above, input slot, optional error / hint below.
 *  Generates a stable id and exposes it through context so child primitives
 *  (Input/Select/Textarea) auto-wire `id`, `aria-invalid`, and `aria-describedby`
 *  without each call site repeating the boilerplate. */
export function Field({
  label,
  htmlFor,
  hint,
  labelIcon,
  helpTooltip,
  error,
  required,
  children,
  className,
}: {
  label?: ReactNode
  htmlFor?: string
  hint?: ReactNode
  /** Optional leading icon (Lucide node) rendered before the label text.
   * Pre-icon = contextual cue for what the field represents (User for
   * "Reservado por", Phone for "Teléfono", etc.). Visual rhythm pairs
   * with the post-icon (`helpTooltip`) which is reserved for help. */
  labelIcon?: ReactNode
  /** Long-form helper text shown via a click-to-open Info popover next to
   * the label. Always rendered as the POST-icon — that slot is reserved
   * for help so the visual pattern stays consistent across the app.
   * Touch-friendly: opens on click; hover also reveals via the popover. */
  helpTooltip?: ReactNode
  error?: ReactNode
  /** Marks the field required: renders a `*` after the label and auto-wires
   *  `aria-required` on the child input/select/textarea. */
  required?: boolean
  children: ReactNode
  className?: string
}) {
  const reactId = useId()
  const fieldId = htmlFor ?? `f-${reactId}`
  const errorId = error ? `${fieldId}-err` : undefined
  const hintId = hint && !error ? `${fieldId}-hint` : undefined
  const ctx: FieldCtx = {
    id: fieldId,
    describedBy: errorId ?? hintId,
    invalid: !!error,
    required: !!required,
  }
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn('field', error && 'has-error', className)}>
        {label && (
          <label className="field-label" htmlFor={fieldId}>
            {labelIcon ? <span className="field-label-icon" aria-hidden="true">{labelIcon}</span> : null}
            <span className="field-label-text">{label}</span>
            {required ? (
              <span className="field-required" aria-hidden="true">
                *
              </span>
            ) : null}
            {helpTooltip ? <InfoTooltip title={helpTooltip} /> : null}
          </label>
        )}
        {children}
        {error ? (
          <div id={errorId} className="field-error" role="alert">
            {error}
          </div>
        ) : hint ? (
          <div id={hintId} className="field-hint">
            {hint}
          </div>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

export const Label = ({ className, children, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('field-label', className)} {...rest}>
    {children}
  </label>
)

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Size variant. md (32 px) is the default; lg (36 px) for primary forms; sm (28 px) for dense filter rows. */
  size?: InputSize
  /** Icon component (Lucide) or any node rendered inside the leading slot. */
  leadingIcon?: ReactNode
  /** Icon component (Lucide) or any node rendered inside the trailing slot. */
  trailingIcon?: ReactNode
  /** Static text addon left of the input (e.g. "€", "https://"). */
  addonStart?: ReactNode
  /** Static text addon right of the input (e.g. "/mo", "h"). */
  addonEnd?: ReactNode
  /** When true, shows an X button when value is non-empty that fires a synthetic onChange with ''. */
  clearable?: boolean
  /** When true, shows a spinner in the trailing slot and sets aria-busy. */
  loading?: boolean
  /** Aria-label for the clear button. Default: "Limpiar". */
  clearAriaLabel?: string
  /** Optional invalid override — when provided, takes precedence over Field context. */
  invalid?: boolean
}

const sizeClass: Record<InputSize, string> = {
  sm: 'input--sm',
  md: '',
  lg: 'input--lg',
}

/** Native input + setter dance — the React-canonical way to programmatically
 *  trigger an onChange that controlled callers will receive correctly. */
function emitValueChange(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    size = 'md',
    leadingIcon,
    trailingIcon,
    addonStart,
    addonEnd,
    clearable,
    loading,
    clearAriaLabel = 'Limpiar',
    invalid: invalidProp,
    id: idProp,
    'aria-describedby': describedByProp,
    'aria-invalid': ariaInvalidProp,
    'aria-required': ariaRequiredProp,
    disabled,
    value,
    defaultValue,
    onChange,
    ...rest
  },
  ref,
) {
  const ctx = useContext(FieldContext)
  const id = idProp ?? ctx?.id
  const describedBy = describedByProp ?? ctx?.describedBy
  const invalid = invalidProp ?? ctx?.invalid ?? false
  const ariaInvalid = ariaInvalidProp ?? (invalid ? true : undefined)
  const ariaRequired = ariaRequiredProp ?? (ctx?.required ? true : undefined)

  const innerRef = useRef<HTMLInputElement | null>(null)
  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) (ref as { current: HTMLInputElement | null }).current = node
    },
    [ref],
  )

  const hasAdornment =
    !!leadingIcon || !!trailingIcon || !!addonStart || !!addonEnd || !!loading || !!clearable

  // Plain input — preserves the existing 140-call-site contract, zero wrapper churn.
  if (!hasAdornment) {
    return (
      <input
        ref={setRef}
        id={id}
        className={cn('input', sizeClass[size], className)}
        aria-invalid={ariaInvalid}
        aria-describedby={describedBy}
        aria-required={ariaRequired}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        {...rest}
      />
    )
  }

  // Detect "has value" for clearable: works for both controlled and uncontrolled.
  const isControlled = value !== undefined
  const hasValue = isControlled
    ? value !== null && value !== '' && value !== undefined
    : !!innerRef.current?.value

  const handleClear = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!innerRef.current) return
    emitValueChange(innerRef.current, '')
    innerRef.current.focus()
  }

  return (
    <div
      className={cn(
        'input-shell',
        sizeClass[size] && `input-shell--${size}`,
        invalid && 'is-invalid',
        disabled && 'is-disabled',
        className,
      )}
      aria-busy={loading || undefined}
    >
      {leadingIcon ? <span className="input-shell__icon">{leadingIcon}</span> : null}
      {addonStart ? <span className="input-shell__addon">{addonStart}</span> : null}
      <input
        ref={setRef}
        id={id}
        className="input-shell__input"
        aria-invalid={ariaInvalid}
        aria-describedby={describedBy}
        aria-required={ariaRequired}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        {...rest}
      />
      {addonEnd ? <span className="input-shell__addon">{addonEnd}</span> : null}
      {clearable && hasValue && !loading && !disabled ? (
        <button
          type="button"
          className="input-shell__clear"
          onClick={handleClear}
          aria-label={clearAriaLabel}
          tabIndex={-1}
        >
          <XIcon size={12} />
        </button>
      ) : null}
      {loading ? (
        <span className="input-shell__icon input-shell__icon--spin">
          <Loader2 size={14} />
        </span>
      ) : trailingIcon ? (
        <span className="input-shell__icon">{trailingIcon}</span>
      ) : null}
    </div>
  )
})

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: InputSize
  invalid?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, size = 'md', invalid: invalidProp, id: idProp, 'aria-describedby': describedByProp, 'aria-invalid': ariaInvalidProp, 'aria-required': ariaRequiredProp, children, ...rest },
  ref,
) {
  const ctx = useContext(FieldContext)
  const id = idProp ?? ctx?.id
  const describedBy = describedByProp ?? ctx?.describedBy
  const invalid = invalidProp ?? ctx?.invalid ?? false
  const ariaInvalid = ariaInvalidProp ?? (invalid ? true : undefined)
  const ariaRequired = ariaRequiredProp ?? (ctx?.required ? true : undefined)
  return (
    <select
      ref={ref}
      id={id}
      className={cn('input select', sizeClass[size], className)}
      aria-invalid={ariaInvalid}
      aria-describedby={describedBy}
      aria-required={ariaRequired}
      {...rest}
    >
      {children}
    </select>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid: invalidProp, id: idProp, 'aria-describedby': describedByProp, 'aria-invalid': ariaInvalidProp, 'aria-required': ariaRequiredProp, ...rest },
  ref,
) {
  const ctx = useContext(FieldContext)
  const id = idProp ?? ctx?.id
  const describedBy = describedByProp ?? ctx?.describedBy
  const invalid = invalidProp ?? ctx?.invalid ?? false
  const ariaInvalid = ariaInvalidProp ?? (invalid ? true : undefined)
  const ariaRequired = ariaRequiredProp ?? (ctx?.required ? true : undefined)
  return (
    <textarea
      ref={ref}
      id={id}
      className={cn('input textarea', className)}
      aria-invalid={ariaInvalid}
      aria-describedby={describedBy}
      aria-required={ariaRequired}
      {...rest}
    />
  )
})

/** Test/integration helper — read the Field-provided id/describedBy from outside the primitive set. */
export function useFieldContext(): FieldCtx | null {
  return useContext(FieldContext)
}
